import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./Table";
import { User, UserRole } from '@/types';
import { useToast } from '@/contexts/ToastContext';
import { API_BASE_URL } from '@/config';
import { useLogger } from '@/contexts/LogContext';
import { LogLevel, LogCategory } from '@/types/logs';
import { Loader2, Search } from 'lucide-react';
import { useWallet } from '@/contexts/WalletContext';
import { contractAddresses } from '@/data/contractAddresses';
import KalaKrutTokenABI from '@/abis/KalaKrutToken.json';
import { ethers, EventLog } from 'ethers';

interface VenueRealDataTableProps {
  currentUserRole: UserRole;
  currentUserId: string;
}

const ActionButton: React.FC<{ onClick: () => void; className: string; children: React.ReactNode }> = ({ onClick, className, children }) => (
  <button
    onClick={onClick}
    className={`text-xs font-semibold px-2.5 py-1 rounded-md transition-colors ${className}`}>
    {children}
  </button>
);

const TableActions: React.FC<{ 
  venue: User;
  role: UserRole; 
  onAction: (action: string, venueId: string) => void;
}> = ({ venue, role, onAction }) => {
  const handleAction = (action: string) => onAction(action, venue.id);
  const isAdmin = role === UserRole.ADMIN || role === UserRole.SYSTEM_ADMIN_LIVE;
  
  if (isAdmin) {
    return (
        <div className="space-x-2">
          <ActionButton onClick={() => handleAction('edit')} className="bg-blue-500/20 text-blue-300 hover:bg-blue-500/40">Edit</ActionButton>
          <ActionButton onClick={() => handleAction('delete')} className="bg-red-500/20 text-red-300 hover:bg-red-500/40">Delete</ActionButton>
        </div>
      );
  }
  return <></>;
};

const VenueRealDataTable: React.FC<VenueRealDataTableProps> = ({ currentUserRole, currentUserId }) => {
  const [allVenues, setAllVenues] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [isFallback, setIsFallback] = useState(false);
  const { notify } = useToast();
  const { addLog } = useLogger();
  const { provider, isConnected } = useWallet();
  const PAGE_SIZE = 10;

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setIsFallback(false);
    addLog("Starting venue data fetch.", LogLevel.INFO, LogCategory.DATA);

    if (provider && isConnected) {
      try {
        const tokenContract = new ethers.Contract(contractAddresses.KalaKrutToken, KalaKrutTokenABI.abi, provider);
        const transferFilter = tokenContract.filters.Transfer(null, null, null);
        const transferEvents = await tokenContract.queryFilter(transferFilter);

        const venueAddresses = [...new Set(transferEvents.map((event: EventLog) => event.args.to))];
        
        const venuePromises = venueAddresses.map(async (address: string) => {
          const response = await fetch(`${API_BASE_URL}/api/users?walletAddress=${address}&role=VENUE`, { credentials: 'include' });
          if (response.ok) return response.json();
          return { id: address, walletAddress: address, name: 'On-Chain Venue', role: UserRole.VENUE, venueProfile: { capacity: 'N/A' } };
        });

        const venues = (await Promise.all(venuePromises)).flat().filter((a: User) => a);
        setAllVenues(venues);
        addLog(`Fetched ${venues.length} on-chain venues.`, LogLevel.SUCCESS, LogCategory.DATA);
        setIsLoading(false);
        return;
      } catch (e) {
        const onChainError = "Could not load on-chain venue data. Falling back to API.";
        addLog(`On-chain fetch failed: ${(e as Error).message}`, LogLevel.ERROR, LogCategory.DATA);
        setError(onChainError);
        notify(onChainError, 'warn');
        setIsFallback(true);
      }
    }

    // Fallback to Off-Chain API
    try {
      let apiData;
      const response = await fetch(`${API_BASE_URL}/api/users?role=VENUE`, { credentials: 'include' });

      if (response.ok) {
        apiData = await response.json();
        addLog(`Fetched ${apiData.length} venues from REAL API as fallback.`, LogLevel.INFO, LogCategory.API);
      } else {
        addLog(`Real API fallback failed, using mock data.`, LogLevel.WARN, LogCategory.API);
        const mockResponse = await fetch('/venues.json');
        if (!mockResponse.ok) throw new Error('Real API and mock data both failed.');
        apiData = await mockResponse.json();
        notify('Displaying mock data. Wallet not connected and API failed.', 'warn');
      }
      
      if (Array.isArray(apiData)) {
        setAllVenues(apiData);
        setIsFallback(true);
      } else {
        throw new Error('Fetched off-chain data is not an array.');
      }

    } catch (e) {
      const fallbackError = `Critical: Failed to load any venue data: ${(e as Error).message}`;
      addLog(fallbackError, LogLevel.CRITICAL, LogCategory.API);
      setError(fallbackError);
      notify(fallbackError, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [provider, isConnected, notify, addLog]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAction = async (action: string, venueId: string) => {
    if (action === 'delete') {
      if (!window.confirm('Are you sure you want to delete this venue?')) return;
      addLog(`Admin ${currentUserId} initiated deletion of venue ${venueId}`, LogLevel.INFO, LogCategory.VENUE);
      try {
        const response = await fetch(`${API_BASE_URL}/api/users/${venueId}`, { method: 'DELETE', credentials: 'include' });
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to delete venue via API.');
        }
        setAllVenues(current => current.filter(a => a.id !== venueId));
        notify(`Venue ${venueId} was successfully deleted.`, 'success');
        addLog(`Venue ${venueId} successfully deleted by ${currentUserId}.`, LogLevel.INFO, LogCategory.VENUE);
      } catch (err) {
        notify((err as Error).message, 'error');
        addLog(`Error deleting venue ${venueId}: ${(err as Error).message}`, LogLevel.ERROR, LogCategory.VENUE);
      }
    } else {
      notify(`Action '${action}' for venue '${venueId}' is not implemented.`, 'info');
    }
  };

  const filteredVenues = useMemo(() => {
    return allVenues.filter(a => 
      (a.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (a.venueProfile?.capacity?.toString() || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [allVenues, searchTerm]);

  const displayedVenues = useMemo(() => {
      return filteredVenues.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  }, [filteredVenues, page]);

  const hasMore = page * PAGE_SIZE < filteredVenues.length;

  if (isLoading && allVenues.length === 0) return (
    <div className="flex justify-center items-center h-48">
      <Loader2 className="w-8 h-8 text-kala-secondary animate-spin" />
      <p className="ml-4 text-kala-400">Loading venues...</p>
    </div>
  );

  return (
    <div className="bg-kala-900 text-white rounded-lg p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">Venues</h2>
        <div className="relative">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-kala-500" />
            <input
                type="text"
                placeholder="Search by name or capacity..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="bg-kala-800 border border-kala-700 rounded-full pl-10 pr-4 py-2 text-sm text-white focus:ring-1 focus:ring-kala-secondary outline-none w-64"
            />
        </div>
      </div>
      {error && <div className="p-4 mb-4 bg-yellow-500/10 text-yellow-400 text-center text-sm rounded-lg">Warning: {error}</div>}
      {(!isConnected || isFallback) && (
        <div className="p-2 mb-4 bg-kala-900/30 text-kala-500 text-center text-[10px] uppercase tracking-widest border-y border-kala-700">
          Note: Displaying fallback data. For real-time information, connect your wallet.
        </div>
      )}

      <Table>
        <TableCaption>A list of venues in the roster. {allVenues.length === 0 && !isLoading && "No venues found."}</TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Capacity</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {displayedVenues.map((venue) => (
            <TableRow key={venue.id}>
              <TableCell>{venue.name || 'N/A'}</TableCell>
              <TableCell>{venue.venueProfile?.capacity || 'N/A'}</TableCell>
              <TableCell className="text-right">
                <TableActions venue={venue} role={currentUserRole} onAction={handleAction} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

        {displayedVenues.length === 0 && !isLoading && (
            <div className="text-center p-8 text-kala-500">
                <p>No venues found for your search term.</p>
            </div>
        )}

      <div className="flex justify-between items-center mt-6">
        <button 
          onClick={() => setPage(p => Math.max(1, p - 1))}
          disabled={page === 1 || isLoading}
          className="px-4 py-2 bg-kala-700 hover:bg-kala-600 text-white font-bold rounded-lg disabled:opacity-50 transition-colors"
        >
          Previous
        </button>
        <span className="text-kala-400">Page {page} of {Math.ceil(filteredVenues.length / PAGE_SIZE)}</span>
        <button 
          onClick={() => setPage(p => p + 1)}
          disabled={!hasMore || isLoading}
          className="px-4 py-2 bg-kala-700 hover:bg-kala-600 text-white font-bold rounded-lg disabled:opacity-50 transition-colors"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default VenueRealDataTable;
